import { useState, useEffect } from 'react'

export interface AdminInfo {
  isAdmin: boolean
  role: string
  permissions: string[]
}

export function useAdmin() {
  const [adminInfo, setAdminInfo] = useState<AdminInfo>({
    isAdmin: false,
    role: 'user',
    permissions: []
  })

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Simple admin detection - in a real app this would check JWT token
    // For demo purposes, we'll check for specific Telegram user IDs or a special flag
    const checkAdminStatus = () => {
      try {
        const tg = window.Telegram?.WebApp
        if (tg?.initDataUnsafe?.user) {
          const userId = tg.initDataUnsafe.user.id

          // Demo admin user IDs (replace with real logic)
          const adminUserIds = [123456789, 987654321] // Replace with actual admin user IDs

          // Check if current user is in admin list
          const isAdmin = adminUserIds.includes(userId)

          // Or check for a debug mode (for testing)
          const isDebugAdmin = localStorage.getItem('debug_admin') === 'true'

          setAdminInfo({
            isAdmin: isAdmin || isDebugAdmin,
            role: isAdmin || isDebugAdmin ? 'admin' : 'user',
            permissions: isAdmin || isDebugAdmin ? ['manage_products', 'manage_orders', 'view_analytics'] : []
          })
        } else {
          // Fallback: Check for debug mode when not in Telegram
          const isDebugAdmin = localStorage.getItem('debug_admin') === 'true'
          setAdminInfo({
            isAdmin: isDebugAdmin,
            role: isDebugAdmin ? 'admin' : 'user',
            permissions: isDebugAdmin ? ['manage_products', 'manage_orders', 'view_analytics'] : []
          })
        }
      } catch (error) {
        console.warn('Failed to check admin status:', error)
        setAdminInfo({
          isAdmin: false,
          role: 'user',
          permissions: []
        })
      } finally {
        setLoading(false)
      }
    }

    checkAdminStatus()
  }, [])

  // Toggle debug admin mode (for testing)
  const toggleDebugAdmin = () => {
    const currentValue = localStorage.getItem('debug_admin') === 'true'
    const newValue = !currentValue
    localStorage.setItem('debug_admin', newValue.toString())

    setAdminInfo(prev => ({
      ...prev,
      isAdmin: newValue,
      role: newValue ? 'admin' : 'user',
      permissions: newValue ? ['manage_products', 'manage_orders', 'view_analytics'] : []
    }))
  }

  return {
    ...adminInfo,
    loading,
    toggleDebugAdmin
  }
}